import React, { useState, useEffect, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || '';

/**
 * EditSearchDrawer — an in-layout right-side panel for editing a
 * search/pipeline's core fields. Rendered via AppShell's `rightPanel` slot,
 * so it pushes page content and sits *under* the nav header (a flex sibling
 * of <main>), matching the assistant/utility sidebars — NOT a floating
 * overlay. Mounted only when open, so it needs no scrim/open logic.
 */
export default function EditSearchDrawer({ pipeline, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reformatting, setReformatting] = useState(false);
  const [reformatMsg, setReformatMsg] = useState(null);
  const firstFieldRef = useRef(null);

  const reformatExisting = async () => {
    if (!pipeline?.id) return;
    setReformatting(true);
    setReformatMsg(null);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      // Save current format first so the reformat uses it.
      await save(true);
      const resp = await fetch(`${API}/api/pipelines/${pipeline.id}/reformat-codes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) throw new Error('Reformat failed');
      const r = await resp.json();
      setReformatMsg(`Reformatted ${r.updated} code${r.updated === 1 ? '' : 's'}.`);
    } catch (e) {
      setReformatMsg('Could not reformat existing codes.');
    } finally {
      setReformatting(false);
    }
  };

  useEffect(() => {
    if (pipeline) {
      setForm({
        name: pipeline.name || '',
        status: pipeline.status || 'active',
        location: pipeline.location || '',
        compensation_range: pipeline.compensation_range || '',
        description: pipeline.description || '',
        requirements: pipeline.requirements || '',
        code_prefix: pipeline.code_prefix || '',
        code_connector: pipeline.code_connector === null || pipeline.code_connector === undefined ? '-' : pipeline.code_connector,
        code_digits: pipeline.code_digits || 2,
      });
      setError(null);
      setTimeout(() => firstFieldRef.current?.focus(), 120);
    }
  }, [pipeline]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const dirty = pipeline && (
    form.name !== (pipeline.name || '') ||
    form.status !== (pipeline.status || 'active') ||
    form.location !== (pipeline.location || '') ||
    form.compensation_range !== (pipeline.compensation_range || '') ||
    form.description !== (pipeline.description || '') ||
    form.requirements !== (pipeline.requirements || '') ||
    (form.code_prefix || '') !== (pipeline.code_prefix || '') ||
    (form.code_connector ?? '-') !== (pipeline.code_connector ?? '-') ||
    (form.code_digits || 2) !== (pipeline.code_digits || 2)
  );

  // Live preview of the code format: prefix + connector + zero-padded number.
  const previewCode = (() => {
    const pfx = (form.code_prefix && form.code_prefix.trim())
      ? form.code_prefix.trim().toUpperCase()
      : (form.name || 'SR').split(/\s+/).filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 3);
    const conn = form.code_connector ?? '-';
    const d = Math.min(4, Math.max(1, form.code_digits || 2));
    return `${pfx}${conn}${String(7).padStart(d, '0')}`;
  })();

  const save = async (keepOpen = false) => {
    if (!pipeline?.id || !form.name?.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const resp = await fetch(`${API}/api/pipelines/${pipeline.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          status: form.status,
          location: form.location,
          compensation_range: form.compensation_range,
          description: form.description,
          requirements: form.requirements,
          code_prefix: (form.code_prefix || '').trim().toUpperCase(),
          code_connector: form.code_connector ?? '-',
          code_digits: Math.min(4, Math.max(1, form.code_digits || 2)),
        }),
      });
      if (!resp.ok) throw new Error('Save failed');
      const updated = await resp.json();
      onSaved?.(updated);
      if (!keepOpen) onClose();
    } catch (e) {
      setError('Could not save changes. Please try again.');
      throw e;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full w-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 h-11 px-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-900">Edit search</h2>
        <button onClick={() => !saving && onClose()} className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200" title="Close">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {error && <div className="px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}

        <Field label="Name" required>
          <input ref={firstFieldRef} value={form.name || ''} onChange={e => set('name', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <select value={form.status || 'active'} onChange={e => set('status', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500">
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="closed">Closed</option>
              <option value="placement">Placement</option>
              <option value="cancelled">Cancelled</option>
              <option value="internal_hire">Internal Hire</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
          <Field label="Location">
            <input value={form.location || ''} onChange={e => set('location', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" />
          </Field>
        </div>

        <Field label="Compensation range">
          <input value={form.compensation_range || ''} onChange={e => set('compensation_range', e.target.value)}
            placeholder="e.g. $150k-$200k + equity"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" />
        </Field>

        <Field label="Description">
          <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows={5}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-y focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" />
        </Field>

        <Field label="Requirements">
          <textarea value={form.requirements || ''} onChange={e => set('requirements', e.target.value)} rows={5}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-y focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" />
        </Field>

        {/* Search ID Code format */}
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">Search ID code format</span>
            <span className="text-xs text-gray-500">Preview: <span className="font-mono font-medium text-gray-800">{previewCode}</span></span>
          </div>
          <p className="text-[11px] text-gray-400 mt-0.5 mb-2">Controls how candidate codes are built for this search.</p>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Prefix">
              <input
                value={form.code_prefix || ''}
                onChange={e => set('code_prefix', e.target.value.toUpperCase().slice(0, 12))}
                placeholder="auto"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 font-mono uppercase focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </Field>
            <Field label="Connector">
              <select value={form.code_connector ?? '-'} onChange={e => set('code_connector', e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500">
                <option value="-">Hyphen ( - )</option>
                <option value="_">Underscore ( _ )</option>
                <option value=".">Period ( . )</option>
                <option value="/">Slash ( / )</option>
                <option value=" ">Space (   )</option>
                <option value="">None</option>
              </select>
            </Field>
            <Field label="Digits">
              <select value={form.code_digits || 2} onChange={e => set('code_digits', parseInt(e.target.value, 10))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500">
                <option value={1}>1 (5)</option>
                <option value={2}>2 (05)</option>
                <option value={3}>3 (005)</option>
                <option value={4}>4 (0005)</option>
              </select>
            </Field>
          </div>

          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={reformatExisting}
              disabled={reformatting || saving}
              className="text-xs font-medium px-2.5 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Save this format and re-apply it to all existing codes in this search"
            >
              {reformatting ? 'Reformatting...' : 'Reformat existing codes'}
            </button>
            {reformatMsg && <span className="text-xs text-gray-500">{reformatMsg}</span>}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">New codes use this format automatically. Use "Reformat" to update codes already assigned.</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 h-16 px-4 border-t border-gray-200 flex items-center justify-end gap-2">
        <button onClick={() => !saving && onClose()} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100">Cancel</button>
        <button onClick={() => save(false)} disabled={saving || !dirty || !form.name?.trim()}
          className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600">{label}{required && <span className="text-red-500"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
